import { createPortal } from 'react-dom';
import { FC, RefObject } from 'react';
import classNames from 'classnames';

export interface ActionMenuPopupItem {
  /** Текст пункта меню */
  label: string;
  /** Обработчик клика (вызывается при выборе пункта) */
  onClick: () => void;
  /** Вариант отображения: по умолчанию — чёрный текст, danger — красный (для деструктивных действий) */
  variant?: 'default' | 'danger';
}

export interface ActionMenuPopupProps {
  /** Пункты меню: текст и действие при клике */
  items: ActionMenuPopupItem[];
  /** Позиция меню (bottom, right от viewport) — обычно из getBoundingClientRect триггера */
  position: { bottom: number; right: number };
  /** Ref на контейнер меню — для обработки клика снаружи (например, закрытие) */
  contentRef?: RefObject<HTMLDivElement | null>;
  /** Ширина меню в пикселях */
  width?: number;
  /** Дополнительный класс контейнера */
  className?: string;
  /** z-index слоя */
  zIndex?: number;
}

/**
 * Всплывающее меню действий (белая панель с пунктами).
 * Рендерится в document.body через createPortal.
 * Переиспользуемый: передаёте массив пунктов с текстом и onClick.
 */
export const ActionMenuPopup: FC<ActionMenuPopupProps> = ({
  items,
  position,
  contentRef,
  width = 260,
  className,
  zIndex = 10000,
}) => {
  if (items.length === 0) return null;

  return createPortal(
    <div
      ref={contentRef as RefObject<HTMLDivElement>}
      className={classNames(
        'fixed rounded-[10px] bg-white p-4 shadow-lg cursor-pointer',
        className,
      )}
      style={{
        bottom: position.bottom,
        right: position.right,
        width,
        zIndex,
      }}
      role="menu"
    >
      {items.map((item, index) => (
        <button
          key={index}
          type="button"
          role="menuitem"
          className={classNames(
            'w-full px-4 py-4 text-left text-sm cursor-pointer transition-colors',
            item.variant === 'danger'
              ? 'text-red-600 hover:bg-red-50'
              : 'text-[#2C3038] hover:bg-[#F5F5F5]',
          )}
          onClick={item.onClick}
        >
          {item.label}
        </button>
      ))}
    </div>,
    document.body,
  );
};
